import {
  MAX_TICKET_MESSAGE,
  TICKET_STATUS_LABELS,
  TICKET_STATUSES,
  type TicketDTO,
  type TicketMessageDTO,
  type TicketReplyBody,
  type TicketStatus,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { ArrowLeft, CircleDot, Mic, SearchX, Send, ShieldCheck } from 'lucide-react';
import { type FormEvent, useEffect, useId } from 'react';
import { Link, useParams } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Avatar, Badge, Button, Card, EmptyState, Field, PageSpinner, Select, VoiceTextarea, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, ApiError, errorMessage, fieldErrors } from '../../lib/api';
import { TicketKindLabel, TicketStatusBadge } from './TicketBadges';
import { AttachmentDropZone, AttachmentPicker, MessageAttachments, ticketRequest } from './attachments';
import { useMessageDraft } from './useMessageDraft';

const STATUS_OPTIONS = TICKET_STATUSES.map((status) => ({ value: status, label: TICKET_STATUS_LABELS[status] }));

/** One note: the conversation with the administrators, a reply box, and (for administrators) the status. */
export function NoteDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const statusId = useId();

  const { data: ticket, error, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['tickets', id],
    queryFn: ({ signal }) => api.get<TicketDTO>(`/api/tickets/${encodeURIComponent(id)}`, { signal }),
  });

  // Opening the note marked it read on the server: refresh the sidebar badge and the list's unread dots.
  useEffect(() => {
    if (dataUpdatedAt === 0) return;
    void queryClient.invalidateQueries({ queryKey: ['tickets', 'unread'] });
    void queryClient.invalidateQueries({ queryKey: ['tickets', 'list'] });
  }, [dataUpdatedAt, queryClient]);

  const setStatus = useMutation({
    mutationFn: (status: TicketStatus) => api.patch<TicketDTO>(`/api/tickets/${encodeURIComponent(id)}`, { status }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['tickets', id], updated);
      toast.success(`Marked ${TICKET_STATUS_LABELS[updated.status]}`);
    },
    onError: (err) => toast.error(err),
  });

  if (isLoading) return <PageSpinner />;
  if (!ticket) {
    const missing = error instanceof ApiError && (error.status === 404 || error.status === 400);
    return (
      <Page width="narrow">
        <EmptyState
          icon={SearchX}
          title={missing ? 'This note doesn’t exist' : 'Couldn’t load this note'}
          description={missing ? 'It may have been removed, or it belongs to someone else.' : errorMessage(error)}
          action={
            <Link to="/notes" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ember-700 hover:underline">
              <ArrowLeft size={14} aria-hidden /> Back to Notes
            </Link>
          }
        />
      </Page>
    );
  }

  return (
    <Page width="narrow">
      <PageHeader
        breadcrumbs={[{ label: 'Notes', to: '/notes' }, { label: `#${ticket.number}` }]}
        title={
          <>
            <span className="font-normal text-slate-400 tabular">#{ticket.number}</span>{' '}
            {ticket.title}
          </>
        }
        documentTitle={`#${ticket.number} ${ticket.title}`}
        meta={<TicketStatusBadge status={ticket.status} size="md" />}
        description={
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <TicketKindLabel kind={ticket.kind} />
            <span aria-hidden>·</span>
            <span>Sent by {ticket.createdBy?.name ?? 'a former user'}</span>
            <RelativeTime value={ticket.createdAt} />
          </span>
        }
        actions={
          ticket.canManage && (
            <div className="flex items-center gap-2">
              <label htmlFor={statusId} className="text-[13px] font-medium text-slate-600">
                Status
              </label>
              <Select
                id={statusId}
                value={ticket.status}
                options={STATUS_OPTIONS}
                disabled={setStatus.isPending}
                onChange={(event) => setStatus.mutate(event.target.value as TicketStatus)}
                className="w-36"
              />
            </div>
          )
        }
      />

      <ol aria-label="Conversation" className="flex flex-col gap-3">
        {ticket.messages.map((message) => (
          <li key={message.id}>
            {message.newStatus ? <StatusLine message={message} status={message.newStatus} /> : <Message ticketId={ticket.id} message={message} />}
          </li>
        ))}
      </ol>

      <ReplyBox ticket={ticket} />
    </Page>
  );
}

function Message({ ticketId, message }: { ticketId: string; message: TicketMessageDTO }) {
  const name = message.author?.name ?? 'Former user';
  return (
    <article className={clsx('rounded-lg border p-3 shadow-xs sm:p-4', message.fromAdmin ? 'border-indigo-200 bg-indigo-50/60' : 'border-slate-200 bg-surface')}>
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Avatar name={name} size="sm" />
        <span className="text-[13px] font-medium text-slate-900">{name}</span>
        {message.fromAdmin && (
          <Badge color="indigo" icon={ShieldCheck}>
            Administrator
          </Badge>
        )}
        <RelativeTime value={message.createdAt} className="text-xs text-slate-500" />
      </header>
      <p className="mt-2 text-[13px] leading-5 break-words whitespace-pre-wrap text-slate-800">{message.body}</p>
      <MessageAttachments ticketId={ticketId} attachments={message.attachments} />
      {message.dictated && (
        <p className="mt-2 inline-flex items-center gap-1 text-2xs text-slate-500" title="Some of this was dictated, so a word may have been misheard">
          <Mic size={11} aria-hidden /> dictated
        </p>
      )}
    </article>
  );
}

/** A status change: one compact line in the thread. */
function StatusLine({ message, status }: { message: TicketMessageDTO; status: TicketStatus }) {
  return (
    <p className="flex flex-wrap items-center gap-1.5 px-1 text-xs text-slate-500">
      <CircleDot size={13} aria-hidden className="text-slate-400" />
      <span>
        <span className="font-medium text-slate-700">{message.author?.name ?? 'An administrator'}</span> marked this
      </span>{' '}
      <TicketStatusBadge status={status} />{' '}
      <span aria-hidden>·</span>{' '}
      <RelativeTime value={message.createdAt} />
    </p>
  );
}

function ReplyBox({ ticket }: { ticket: TicketDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const draft = useMessageDraft();
  const { user } = useAuth();

  const reply = useMutation({
    mutationFn: (request: TicketReplyBody | FormData) => api.post<TicketDTO>(`/api/tickets/${encodeURIComponent(ticket.id)}/messages`, request),
    onSuccess: (updated) => {
      draft.reset();
      queryClient.setQueryData(['tickets', ticket.id], updated);
    },
    onError: (error) => toast.error(error),
  });
  const errors = fieldErrors(reply.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (reply.isPending) return;
    const { body, dictated, files } = draft.take();
    if (body.trim() === '') return;
    reply.mutate(ticketRequest({ body, dictated }, files));
  };

  const mine = ticket.createdBy?.id === user?.id;
  return (
    <AttachmentDropZone onFiles={draft.addFiles} disabled={reply.isPending}>
      <Card>
        <form onSubmit={onSubmit} className="flex flex-col gap-3 p-4">
          <Field label="Your reply" error={errors.body}>
            <VoiceTextarea
              {...draft.boxProps}
              rows={3}
              maxLength={MAX_TICKET_MESSAGE}
              disabled={reply.isPending}
              placeholder={mine ? 'Add details or answer the administrator…' : `Reply to ${ticket.createdBy?.name ?? 'the sender'}…`}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) onSubmit(event);
              }}
            />
          </Field>
          <AttachmentPicker files={draft.files} onAdd={draft.addFiles} onRemove={draft.removeFile} disabled={reply.isPending} />
          <div className="flex justify-end">
            <Button type="submit" variant="primary" icon={Send} loading={reply.isPending} disabled={draft.empty} className="w-full max-sm:h-10 sm:w-auto">
              Send reply
            </Button>
          </div>
        </form>
      </Card>
    </AttachmentDropZone>
  );
}
